import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormErrorComponent } from 'shared/components/form-error/form-error.component';
import { TrimInputDirective } from 'shared/directives/trim-input.directive';
import { CurrentShijiUserInterface } from 'shared/interfaces/current-shiji-user.interface';
import { FormDifferService, ModalService, StorageService, ToastService, UserApiService } from '../../services';
import { SupplierUsersService } from 'users/services/supplier-users.service';
import { User, UserGroup } from 'users/interfaces/user.interface';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { ShijiUsersService } from 'users/services/shiji-users.service';
import { IS_SHIJI_USER_KEY, UserType } from '../../constants/general-constants';
import { NotificationSettings } from 'shared/interfaces/notification-settings.interface';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    imports: [FormErrorComponent, TranslateModule, ReactiveFormsModule, TrimInputDirective, RouterLink],
})
export class ProfileComponent implements OnInit {
    public userProfileForm!: UntypedFormGroup;
    public myUser!: User;
    public supplierName!: string;
    public notifications: NotificationSettings[] = [];
    public userGroups!: UserGroup[] | string;

    constructor(
        public userApiService: UserApiService,
        public translate: TranslateService,
        private supplierUsersGridService: SupplierUsersService,
        private shijiUsersGridService: ShijiUsersService,
        private router: Router,
        private formService: FormDifferService,
        private toastService: ToastService,
        private modalService: ModalService,
        private formBuilder: UntypedFormBuilder,
        private destroyRef: DestroyRef,
        private storageService: StorageService,
    ) {
        this.userProfileForm = this.formBuilder.group({
            firstName: new UntypedFormControl(this.myUser?.firstName, [Validators.required, Validators.maxLength(32)]),
            lastName: new UntypedFormControl(this.myUser?.lastName, [Validators.required, Validators.maxLength(32)]),
            notificationSettings: this.formBuilder.group({}),
        });
    }

    public ngOnInit() {
        const isUserShiji = this.storageService.getFromLocal(IS_SHIJI_USER_KEY) as boolean;

        (isUserShiji ? this.userApiService.getCurrentUserData() : this.userApiService.getCurrentSupplierUserData())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((user) => {
                this.processUserData(user);
            });
    }

    private processUserData(user: CurrentSupplierUserInterface | CurrentShijiUserInterface) {
        if (!user) return;

        this.myUser = user as unknown as User;

        // Notifications
        this.handleUserNotifications(user);

        // First name & Last name
        this.userProfileForm.get('firstName')?.setValue(user.firstName);
        this.userProfileForm.get('lastName')?.setValue(user.lastName);

        this.formService.registerForm(this.userProfileForm);
        this.formService.createDataSnapshot();

        // User groups & company name for supplier users
        if (!user.isShiji) {
            const currentUser = user as CurrentSupplierUserInterface & { userGroupName: string | null };
            this.supplierName = currentUser.companyName;
            this.userGroups = currentUser.userGroupName ? currentUser.userGroupName : this.translate.instant('myUserProfile.noGroups');
            this.formService.createDataSnapshot();
        }
    }

    private handleUserNotifications(user: CurrentSupplierUserInterface | CurrentShijiUserInterface) {
        this.notifications = user.notificationSettings;
        this.userProfileForm.removeControl('notificationSettings');
        this.userProfileForm.addControl('notificationSettings', new UntypedFormGroup({}));
        const notifications = this.userProfileForm.get('notificationSettings') as UntypedFormGroup;
        user.notificationSettings?.forEach((item: NotificationSettings) => {
            notifications.addControl(item.id, new UntypedFormControl(item.isActive));
        });
        this.notifications?.orderBy('notificationType', false);
    }

    public saveChanges() {
        if (this.userProfileForm.valid) {
            const userBody = {
                ...this.formService.getDiffWithSnapshot(),
                userType: this.myUser.userType,
            } as User;

            const observablesStream = [];

            const modifiedControls = Object.keys(this.formService.getDiffWithSnapshot());

            if (modifiedControls.includes('firstName') || modifiedControls.includes('lastName')) {
                const updateCurrentUser =
                    this.myUser.userType === UserType.SHIJI_USER
                        ? this.shijiUsersGridService.updateCurrentShijiUser(userBody)
                        : this.supplierUsersGridService.updateCurrentSupplierUser(userBody);

                updateCurrentUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
                observablesStream.push(updateCurrentUser);
            }

            if (modifiedControls.includes('notificationSettings')) {
                const notificationsBody: NotificationSettings[] = [];

                Object.keys(userBody.notificationSettings ?? []).forEach((key) => {
                    notificationsBody.push({
                        id: key,
                        isActive: this.userProfileForm.value.notificationSettings[key],
                        userId: this.myUser.id,
                    } as NotificationSettings);
                });

                const updateNotificationsSettings = this.myUser.isShiji
                    ? this.shijiUsersGridService.updateNotificationSettings(notificationsBody, true)
                    : this.supplierUsersGridService.updateNotificationSettings(notificationsBody, true);

                updateNotificationsSettings.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
                observablesStream.push(updateNotificationsSettings);
            }

            //No changes were made on the form
            if (!observablesStream.length) {
                this.toastService.showToast('myUserProfile.updateSuccess', 'success');
                this.router.navigate(['/dashboard']);
            } else {
                forkJoin(observablesStream)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        this.toastService.showToast('myUserProfile.updateSuccess', 'success');
                        this.userApiService.getCurrentUser();
                        this.router.navigate(['/dashboard']);
                    });
            }
        }
    }

    public changePassword() {
        this.modalService.openModal(ChangePasswordComponent, { type: 'top' });
    }
}
